import React, { FC, useCallback, ChangeEvent } from 'react'
import moment from 'moment-timezone'
import { Box, makeStyles } from 'transfix-ui/core'
import { useUserContext } from 'apps/fleet-planner/context/UserContext'
import { getClosestTime } from 'apps/fleet-planner/utils/getClosestTime'
import { DatePicker } from 'components/forms'
import { TimeInput, TIME_INPUT_FORMAT } from 'transfix-ui'
import { FORMATS } from 'constants/datetime'

const useDateAndTimePickerStyles = makeStyles(theme => ({
  datePickerContainer: {
    display: 'flex',
    marginRight: theme.spacing(1),
    /**
     * Only set these on desktop
     */
    [theme.breakpoints.up('md')]: {
      minWidth: 122,
      maxWidth: 122,
    },
  },
}))

/**
 * Returns a date string with or without offset
 * @returns i.e. '2021-02-20T00:00:00.000Z` when `withOffset` is [true]
 * @returns i.e. '2021-02-19T19:00:00` when `withOffset` is [false]
 */
const getDateWithOffset = (
  date: moment.Moment,
  timezone: string = moment.tz.guess(),
  withOffset: boolean = false
) => {
  if (withOffset) return date.tz(timezone).toISOString()
  return date.tz(timezone).format(FORMATS.fleetPlannerApiFormat)
}

export interface IDateAndTimePickerProps {
  /**
   * @default true
   */
  large?: boolean
  /**
   * @default false
   */
  wrapValidationText?: boolean
  /**
   * @default TIME_INPUT_FORMAT.MILITARY_SHORT
   */
  timeFormat?: keyof typeof TIME_INPUT_FORMAT
  /**
   * @example 'America/New_York'
   * @default moment.tz.guess()
   */
  timezone?: string
  validationMessage?: string
  dataTest?: string
  /**
   * @default 'Picker'
   */
  ariaLabel?: string
  /**
   * @default 'Date'
   */
  datePlaceholder?: string
  /**
   * Include the offset or not. Sometimes useful for
   * timezone-agnostic components in Fleet Planner
   * @default false
   */
  useOffset?: boolean
  /**
   * ISO Date string
   * @note This value should *not* be null, but we will sanitize it anyway
   * @example '2021-02-12T03:08:15.053Z`
   */
  value?: string | null
  /**
   * Returns object with date and time in the same value.
   * Changes to this single value come from both components: <DatePicker /> and <TimeInput />
   * @param {string} newValue - i.e. '2021-02-12T03:08:15.053Z`
   */
  onChange: (newValue: string | undefined) => void
}

/**
 * A stateless input element which uses parent state to render the
 * same ISO Date value in two different components.
 */
export const DateAndTimePicker: FC<IDateAndTimePickerProps> = ({
  value: valueProp,
  large: largeProp = true,
  validationMessage: validationMessageProp = '',
  wrapValidationText: wrapValidationTextProp = false,
  timeFormat: timeFormatProp = 'MILITARY_SHORT',
  timezone: timezoneProp = moment.tz.guess(),
  onChange: onChangeProp,
  dataTest: dataTestProp = '',
  ariaLabel: ariaLabelProp = 'Picker',
  datePlaceholder: datePlaceholderProp = 'Date',
  useOffset: useOffsetProp = false,
}) => {
  /**
   * Allow `null` as a prop, but sanitize it before
   * using it in the rest of the component.
   *
   * This is to allow for empty values in RHF
   */
  const value = valueProp ?? undefined

  /**
   * Get user defaults for timeInputFormat
   */
  const { timeFormats } = useUserContext()
  const { stopTimeInput: timeFormat = TIME_INPUT_FORMAT[timeFormatProp] } = timeFormats || {}

  /**
   * Handles dates in `flatpickrDateTimeFormat` format, and
   * creates update with ISO Date string format
   */
  const onDateChange = useCallback(
    ([date]: string[]) => {
      const isValuePropValid = !!value && typeof value !== 'undefined' && moment(value).isValid()
      const isDateValid = typeof date !== 'undefined' && moment(date).isValid()
      const prevDate = isValuePropValid
        ? moment(value).tz(timezoneProp)
        : getClosestTime(moment(), timezoneProp)

      if (!isDateValid) {
        const newDate = getDateWithOffset(prevDate, timezoneProp, useOffsetProp)
        onChangeProp(newDate)
        return
      }

      // Only used stored time if it's available in previous value
      const newDate = moment(date, FORMATS.flatpickrDateTimeFormat).tz(timezoneProp)
      newDate
        .set({
          hours: prevDate.hours(),
          minutes: prevDate.minutes(),
        })
        .startOf('minute')
        .tz(timezoneProp)

      const newDateWithOffset = getDateWithOffset(newDate, timezoneProp, useOffsetProp)
      onChangeProp(newDateWithOffset)
    },
    [value, timezoneProp, onChangeProp, useOffsetProp]
  )

  /**
   * Handles changes in `TIME_INPUT_FORMAT` format enum,
   * and creates update in ISO Date string format
   */
  const onTimeChange = useCallback(
    ({ target: { value: time } }: ChangeEvent<HTMLInputElement>) => {
      const isValuePropValid = !!value && typeof value !== 'undefined' && moment(value).isValid()
      const newDate = isValuePropValid
        ? moment(value).tz(timezoneProp)
        : moment().startOf('d').tz(timezoneProp)
      const newTime = moment(time, timeFormat).tz(timezoneProp)
      const isNewTimeValid = typeof time !== 'undefined' && newTime?.isValid()

      /**
       * Handles case where user blurs input but makes no selection
       * In this case, time is an empty string (but not undefined)
       */
      if (!time) {
        return
      }

      // If time value isn't good, just return previous date
      if (!isNewTimeValid) {
        onChangeProp(getDateWithOffset(newDate, timezoneProp, useOffsetProp))
        return
      }

      newDate
        .set({
          hours: newTime.hours(),
          minutes: newTime.minutes(),
        })
        .startOf('minute')
        .tz(timezoneProp)

      const newDateWithOffset = getDateWithOffset(newDate, timezoneProp, useOffsetProp)
      onChangeProp(newDateWithOffset)
    },
    [value, timezoneProp, timeFormat, onChangeProp, useOffsetProp]
  )

  // Formats needed for each component
  const hasValidDate = typeof value !== 'undefined' && moment(value).isValid()
  const momentValue = hasValidDate ? moment(value).tz(timezoneProp) : undefined

  // Helper function to format time, if it's not undefined
  const getFormattedTime = useCallback((): string => {
    if (!momentValue) return ''
    return momentValue.format(timeFormat)
  }, [timeFormat, momentValue])

  // Helper function to format date, if it's not undefined
  const getFormattedDate = useCallback((): string | undefined => {
    if (!momentValue) return undefined
    return momentValue.format(FORMATS.flatpickrDateTimeFormat)
  }, [momentValue])

  const time = getFormattedTime()
  const date = getFormattedDate()

  const classes = useDateAndTimePickerStyles()

  return (
    <Box display='inline-flex' data-test={`${dataTestProp}-container`}>
      <Box className={classes.datePickerContainer}>
        <DatePicker
          large={largeProp}
          date={date}
          placeholder={datePlaceholderProp}
          onDateChange={onDateChange}
          dataTest={`${dataTestProp}-date`}
          ariaLabel={`${ariaLabelProp} Date`}
          validationMessage={validationMessageProp}
          invalid={!!validationMessageProp}
          wrapValidationText={wrapValidationTextProp}
        />
      </Box>
      <Box>
        <TimeInput
          timeFormat={timeFormat}
          value={time}
          onChange={onTimeChange}
          dataTest={`${dataTestProp}-time`}
          ariaLabel={`${ariaLabelProp} Time`}
          invalid={!!validationMessageProp}
          large={largeProp}
        />
      </Box>
    </Box>
  )
}
